import { SectionContainer } from '@/pages/AdvancedEditor/components/panels/common/SectionContainer';
import { StyledText } from '@/pages/AdvancedEditor/components/panels/common/SectionTitle';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { polotnoTypes } from '@/pages/AdvancedEditor/types';
import { checkPolotnoType, getAvatarElementFromPage, getAvatarGroupFromPage } from '@/pages/AdvancedEditor/utils';
import { isStandardAvatar } from '@/pages/avatar/hooks/useAvatarsFilters';
import { useAnalytics } from '@/services/analytics';
import { useFlag } from '@/services/featureFlags';
import theme from '@/theme';
import { MixPanelEvent } from '@/utils/types';
import { FaceRect, Presenter } from '@d-id/types';
import styled from '@emotion/styled';
import { ButtonBase } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { ElementType } from 'polotno/model/group-model';
import { useCallback, useEffect, useState } from 'react';
import { getCloseupFaceData, getFullAvatarData, getSquareFaceData, getViewCache, saveViewCache, setCachedView } from './utils';

const CIRCLE_AVATAR_SIZE = 315;

const svgMask = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="50" fill="black"/>
        </svg>
    `;

const base64Svg = `data:image/svg+xml;base64,${btoa(svgMask)}`;

const ViewButtonContainer = styled.div`
    display: flex;
    padding: 5px;
    border-radius: 8px;
    background: ${theme.colors.black[100]};
`;

const ViewButton = styled(ButtonBase)<{ active?: boolean }>`
    display: flex;
    gap: 6px;
    padding: 7px 8px;
    color: ${theme.colors.secondary[900]};
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    background-color: ${({ active }) => (active ? 'white' : 'transparent')};
    border-radius: 8px;
    border: ${({ theme, active }) => (active ? `1px solid ${theme.colors.black[200]}` : `1px solid transparent`)};
    width: 100%;

    :hover {
        background-color: ${({ theme, active }) => (active ? 'white' : theme.colors.black[200])};
    }
`;

export enum AvatarView {
    Full = 'full',
    CloseUp = 'close-up',
    Circle = 'circle',
}

export const ViewControl = observer(() => {
    const mixpanel = useAnalytics();
    const { store } = useAdvancedEditorContext();
    const circleMaskingEnabled = useFlag('circle_masking_enabled', false);
    const [croppedAvatarRect, setCroppedAvatarRect] = useState<FaceRect | undefined>(undefined);

    const avatarElement = getAvatarElementFromPage(store.activePage);
    const presenter = avatarElement?.custom?.presenter as Presenter | undefined;

    const isStandard = presenter ? isStandardAvatar(presenter) : false;

    useEffect(() => {
        setCroppedAvatarRect(undefined);
    }, [avatarElement]);

    const handleGetPresenterFaceRect = useCallback(async () => {
        if (croppedAvatarRect) return croppedAvatarRect;

        const avatar = presenter;
        if (!avatar?.poster) return;

        // take faces from avatar if exists
        if (avatar?.faceRect) {
            setCroppedAvatarRect(avatar.faceRect);
            return avatar.faceRect;
        }

        setCroppedAvatarRect(undefined);
        return null;
    }, [avatarElement, croppedAvatarRect, setCroppedAvatarRect]);

    const handleNoteViewChange = useCallback(() => {
        avatarElement?.set({
            custom: {
                ...(avatarElement.custom || {}),
                viewModified: true,
            },
        });
    }, [avatarElement]);

    // needs to be hidden in other views than circle
    const changeCircleElemenetsOpacity = (opacity: 1 | 0) => {
        const group = getAvatarGroupFromPage(store.activePage);

        if (group) {
            const circleMaskElements = group.children.filter(
                (child: ElementType) => child.name === 'avatar-border' || child.name === 'avatar-fill'
            );

            circleMaskElements.forEach((element: ElementType) => element.set({ opacity }));
        }
    };

    // needs to be adjusted to currect avatar size
    const adjustCircleElementsSize = () => {
        const group = getAvatarGroupFromPage(store.activePage);

        if (group) {
            const avatarElement = group.children.find((child: ElementType) => checkPolotnoType(child, polotnoTypes.Avatar));
            const circleMaskElements = group.children.filter(
                (child: ElementType) => child.name === 'avatar-border' || child.name === 'avatar-fill'
            );

            circleMaskElements.forEach((element: ElementType) =>
                element.set({ width: avatarElement.width, height: avatarElement.height, x: avatarElement.x, y: avatarElement.y })
            );
        }
    };

    const handleFull = useCallback(() => {
        mixpanel?.track('avatar-masking', { event: MixPanelEvent.CLICK, ['avatar-masking-type']: 'full' });

        if (!avatarElement) return;
        changeCircleElemenetsOpacity(0);
        saveViewCache(avatarElement);

        const viewCache = getViewCache(avatarElement, AvatarView.Full);
        if (viewCache) {
            setCachedView(avatarElement, AvatarView.Full);
            adjustCircleElementsSize();
            return;
        }
        const fullAvatarData = getFullAvatarData(avatarElement);

        avatarElement?.set({
            ...fullAvatarData,
            cropX: 0,
            cropY: 0,
            cropWidth: 1,
            cropHeight: 1,
            clipSrc: '',
            shadowEnabled: false,
            custom: {
                ...(avatarElement.custom || {}),
                view: AvatarView.Full,
            },
        });
        adjustCircleElementsSize();
        handleNoteViewChange();
    }, [avatarElement, handleNoteViewChange]);

    const handleCloseUp = useCallback(async () => {
        mixpanel?.track('avatar-masking', { event: MixPanelEvent.CLICK, ['avatar-masking-type']: 'close-up' });

        if (!avatarElement) return;
        changeCircleElemenetsOpacity(0);
        saveViewCache(avatarElement);

        const viewCache = getViewCache(avatarElement, AvatarView.CloseUp);
        if (viewCache) {
            setCachedView(avatarElement, AvatarView.CloseUp);
            adjustCircleElementsSize();
            return;
        }

        // return to full view in case that circle was before
        handleFull();

        const faceRect = await handleGetPresenterFaceRect();
        const cropData = await getCloseupFaceData(avatarElement, faceRect);

        const originalAvatarX = avatarElement.x + avatarElement.width / 2;
        const originalAvatarY = avatarElement.y + avatarElement.height / 2;

        if (cropData) {
            const ratioX = avatarElement.width / cropData.cropWidth;
            const ratioY = avatarElement.height / cropData.cropHeight;
            const ratio = Math.min(ratioX, ratioY);

            const widthWithRatio = cropData.cropWidth * ratio;
            const heightWithRatio = cropData.cropHeight * ratio;

            avatarElement?.set({
                cropX: cropData.cropXRatio,
                cropY: cropData.cropYRatio,
                cropWidth: cropData.cropWidthRatio,
                cropHeight: cropData.cropHeightRatio,
                width: widthWithRatio,
                height: heightWithRatio,
                x: originalAvatarX - widthWithRatio / 2,
                y: originalAvatarY - heightWithRatio / 2,
                clipSrc: '',
                shadowEnabled: false,
                custom: {
                    ...(avatarElement.custom || {}),
                    view: AvatarView.CloseUp,
                },
            });
            adjustCircleElementsSize();
        }
        handleNoteViewChange();
    }, [avatarElement, handleNoteViewChange]);

    const handleCircleMask = useCallback(async () => {
        mixpanel?.track('avatar-masking', { event: MixPanelEvent.CLICK, ['avatar-masking-type']: 'circle' });

        if (!avatarElement) return;
        changeCircleElemenetsOpacity(1);
        saveViewCache(avatarElement);

        const viewCache = getViewCache(avatarElement, AvatarView.Circle);
        if (viewCache) {
            setCachedView(avatarElement, AvatarView.Circle);
            adjustCircleElementsSize();
            return;
        }

        const faceRect = await handleGetPresenterFaceRect();
        const cropData = isStandard ? null : getSquareFaceData(avatarElement, faceRect);

        const originalAvatarX = avatarElement.x + avatarElement.width / 2;
        const originalAvatarY = avatarElement.y + avatarElement.height / 2;

        if (cropData) {
            avatarElement?.set({
                cropX: cropData.cropXRatio,
                cropY: cropData.cropYRatio,
                cropWidth: cropData.cropWidthRatio,
                cropHeight: cropData.cropHeightRatio,
                width: CIRCLE_AVATAR_SIZE,
                height: CIRCLE_AVATAR_SIZE,
                x: originalAvatarX - CIRCLE_AVATAR_SIZE / 2,
                y: originalAvatarY - CIRCLE_AVATAR_SIZE / 2,
                clipSrc: base64Svg,
                custom: {
                    ...(avatarElement.custom || {}),
                    view: AvatarView.Circle,
                },
            });
            adjustCircleElementsSize();
        } else {
            avatarElement?.set({
                clipSrc: base64Svg,
                custom: {
                    ...(avatarElement.custom || {}),
                    view: AvatarView.Circle,
                },
            });
        }

        handleNoteViewChange();
    }, [avatarElement, isStandard, handleNoteViewChange]);

    const view = avatarElement?.custom?.view;

    if (isStandard && !circleMaskingEnabled) {
        return null;
    }

    return (
        <SectionContainer>
            <StyledText>View</StyledText>
            <ViewButtonContainer>
                <ViewButton onClick={handleFull} active={view === AvatarView.Full}>
                    Full
                </ViewButton>
                {!isStandard && (
                    <ViewButton onClick={handleCloseUp} active={view === AvatarView.CloseUp}>
                        Close up
                    </ViewButton>
                )}
                {circleMaskingEnabled && (
                    <ViewButton onClick={handleCircleMask} active={view === AvatarView.Circle}>
                        Circle
                    </ViewButton>
                )}
            </ViewButtonContainer>
        </SectionContainer>
    );
});
